import { useEffect } from "react";

const DEFAULT_TITLE = "MyMoneyLocal — Personal Finance Calculators & Education";
const DEFAULT_DESCRIPTION =
  "Free, plain-English calculators and guides for borrowing, investing, business, real estate, and vehicles. Make confident money decisions with MyMoneyLocal.";
const SITE_URL = "https://mymoneylocal.com";
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/social-preview.png`;

function upsertTitle(fullTitle) {
  // Remove any duplicate <title> so document.title mutates a single, canonical node.
  const titles = document.head.querySelectorAll("title");
  for (let i = 1; i < titles.length; i++) titles[i].remove();
  document.title = fullTitle;
}

function upsertMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Lightweight SEO hook — sets title, meta description, OG tags, canonical URL.
 * Designed so any future migration to react-helmet / next-head requires no template change.
 */
export function useSeo({ title, description, path, image, type = "website" } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | MyMoneyLocal` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    // Canonicals must always use the production origin and must not include
    // tracking parameters, fragments, or Cloudflare preview hostnames.
    const requestedPath = path || window.location.pathname || "/";
    const canonicalPath = requestedPath === "/" ? "/" : `${requestedPath.replace(/\/+$/, "")}/`;
    const canonicalUrl = new URL(canonicalPath, SITE_URL);
    canonicalUrl.search = "";
    canonicalUrl.hash = "";
    const url = canonicalUrl.toString();
    // Social crawlers need a stable, absolute image URL that returns without JavaScript.
    const img = image ? new URL(image, `${SITE_URL}/`).toString() : DEFAULT_SOCIAL_IMAGE;
    const imageAlt = title || "MyMoneyLocal personal finance calculators and guides";

    upsertTitle(fullTitle);
    upsertMeta("description", desc);
    upsertMeta("og:title", fullTitle, "property");
    upsertMeta("og:description", desc, "property");
    upsertMeta("og:type", type, "property");
    upsertMeta("og:url", url, "property");
    upsertMeta("og:image", img, "property");
    upsertMeta("og:image:type", "image/png", "property");
    upsertMeta("og:image:width", "1200", "property");
    upsertMeta("og:image:height", "630", "property");
    upsertMeta("og:image:alt", imageAlt, "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", fullTitle);
    upsertMeta("twitter:description", desc);
    upsertMeta("twitter:image", img);
    upsertMeta("twitter:image:alt", imageAlt);
    upsertCanonical(url);
  }, [title, description, path, image, type]);
}
